/**
 * InspectorPane — React renderer for InspectorPaneModel
 *
 * Proof-only-safe detail panel for selected governance events.
 * Never exposes raw receipt content.
 *
 * REFINEMENTS (v1.1):
 * - Primary outcome line with verdict
 * - Status capsules for admissibility, continuity, proofOnly, signature
 * - Copy buttons for IDs/hashes
 * - Collapsible receipt preview
 */

import React, { useState, useCallback, useMemo } from 'react';
import type { InspectorPaneModel, AdmissibilityChainEntry } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { severityToColor, continuityToColor, verdictToColor, sourceTagToChipColor, NEUTRAL, TYPOGRAPHY, SPACING, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { inspectorContentStyle } from '../motion/selectionMotionSemantics.js';
import { TRANSITION_COLLAPSE, TRANSITION_TAB_SWITCH } from '../motion/motionTokens.js';

type InspectorTab = 'details' | 'context' | 'evidence' | 'receipts' | 'graph';

const TABS: { id: InspectorTab; label: string }[] = [
  { id: 'details', label: 'Details' },
  { id: 'context', label: 'Context' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'receipts', label: 'Receipts' },
  { id: 'graph', label: 'Graph' },
];

interface InspectorPaneProps {
  model: InspectorPaneModel;
  admissibilityChain?: AdmissibilityChainEntry[];
  replayWindow?: { start: string; end: string; windowId: string } | null;
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [value]);

  return (
    <button
      onClick={handleCopy}
      title={label ? `Copy ${label}` : 'Copy'}
      style={{
        marginLeft: SPACING.sp2,
        padding: '1px 4px',
        fontSize: TYPOGRAPHY.micro.size,
        color: copied ? '#5fa35f' : NEUTRAL.text300,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontFamily: TYPOGRAPHY.mono.family,
      }}
      type="button"
      data-testid={label ? `copy-${label}` : 'copy'}
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.labelSmall.size,
      fontWeight: TYPOGRAPHY.labelSmall.weight,
      color: NEUTRAL.text300,
      marginBottom: SPACING.sp2,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      {children}
    </div>
  );
}

function StatusCapsule({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return (
    <span style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 700,
      color: fg,
      backgroundColor: bg,
      padding: '2px 6px',
      borderRadius: 3,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {text}
    </span>
  );
}

function TabBar({ activeTab, onTabChange }: { activeTab: InspectorTab; onTabChange: (tab: InspectorTab) => void }) {
  return (
    <div style={{
      display: 'flex',
      gap: 0,
      borderBottom: `1px solid ${NEUTRAL.border}`,
      backgroundColor: NEUTRAL.bg600,
      padding: `0 ${SPACING.sp3}px`,
    }}>
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: isActive ? 700 : 600,
              border: 'none',
              borderBottom: isActive ? `2px solid ${NEUTRAL.text600}` : '2px solid transparent',
              backgroundColor: isActive ? NEUTRAL.bg200 : 'transparent',
              color: isActive ? NEUTRAL.text600 : NEUTRAL.text400,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              transition: TRANSITION_TAB_SWITCH,
            }}
            type="button"
            data-testid={`inspector-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function CopyableField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: `${SPACING.sp1}px 0`,
      borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
    }}>
      <span style={{
        width: 100,
        color: NEUTRAL.text400,
        fontSize: TYPOGRAPHY.labelSmall.size,
        flexShrink: 0,
      }}>
        {label}
      </span>
      <span style={{
        color: NEUTRAL.text500,
        fontSize: TYPOGRAPHY.body.size,
        fontFamily: TYPOGRAPHY.mono.family,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
      }}>
        {value}
      </span>
      <CopyButton value={value} label={label} />
    </div>
  );
}

function DetailsTab({ detail }: { detail: InspectorPaneModel['detail'] }) {
  if (!detail) return null;
  const color = severityToColor(detail.severity);
  const sourceChip = sourceTagToChipColor(detail.sourceTag);
  const verdict = detail.refusalDetail
    ? (detail.refusalDetail.verdict as 'PASS' | 'BLOCKED' | 'REJECTED' | 'UNKNOWN') ?? 'BLOCKED'
    : 'PASS';
  const verdictColor = verdictToColor(verdict);
  const continuityColor = detail.continuityMarker ? continuityToColor(detail.continuityMarker) : null;

  return (
    <div style={{ padding: SPACING.sp3 }}>
      <div style={{ marginBottom: SPACING.sp3, paddingBottom: SPACING.sp3, borderBottom: `1px solid ${NEUTRAL.borderSubtle}` }}>
        <div style={{ fontSize: TYPOGRAPHY.caption.size, color: NEUTRAL.text300, marginBottom: SPACING.sp1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {detail.timestamp}
        </div>
        <div style={{ fontSize: TYPOGRAPHY.heading.size, fontWeight: 600, color: color.text, lineHeight: 1.3 }}>
          {detail.eventType}
          {detail.refusalDetail && <span style={{ color: verdictColor.text, marginLeft: SPACING.sp3 }}>{verdict}</span>}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.sp2, marginBottom: SPACING.sp4 }}>
        <StatusCapsule text={detail.severity} bg={color.chip} fg={color.chipText} />
        <StatusCapsule text={detail.sourceTag} bg={sourceChip.chip} fg={sourceChip.chipText} />
        <StatusCapsule text={verdict} bg={verdictColor.chip} fg={verdictColor.chipText} />
        {continuityColor && <StatusCapsule text={detail.continuityMarker!} bg={continuityColor.chip} fg={continuityColor.chipText} />}
        {detail.proofOnly && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 700, color: '#807898', backgroundColor: '#1a1820', padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.5, border: '1px solid #303040' }}>
            restricted
          </span>
        )}
      </div>

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Identity</SectionHeader>
        <CopyableField label="eventId" value={detail.eventId} />
        <CopyableField label="streamId" value={detail.streamId} />
        <CopyableField label="source" value={detail.sourceModule} />
      </div>

      {detail.safeMetadata.length > 0 && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <SectionHeader>Metadata</SectionHeader>
          {detail.safeMetadata.map((meta, i) => <CopyableField key={i} label={meta.key} value={meta.value} />)}
        </div>
      )}

      {detail.refusalDetail && (
        <div style={{ marginBottom: SPACING.sp4, padding: SPACING.sp3, backgroundColor: '#120808', border: '1px solid #6a1818', borderRadius: 3 }}>
          <div style={{ fontSize: TYPOGRAPHY.labelSmall.size, color: '#f04040', marginBottom: SPACING.sp2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Refusal Detail</div>
          <div style={{ color: '#e03030', fontSize: TYPOGRAPHY.body.size }}>{detail.refusalDetail.refusalClass}</div>
          {detail.refusalDetail.errorCode && <div style={{ color: NEUTRAL.text400, fontSize: TYPOGRAPHY.mono.size, marginTop: SPACING.sp1 }}>{detail.refusalDetail.errorCode}</div>}
          {detail.refusalDetail.rationale && <div style={{ color: NEUTRAL.text400, fontSize: TYPOGRAPHY.caption.size, marginTop: SPACING.sp2 }}>{detail.refusalDetail.rationale}</div>}
        </div>
      )}

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Lineage</SectionHeader>
        {detail.lineage.correlationId && <CopyableField label="correlation" value={detail.lineage.correlationId} />}
        {detail.lineage.causationId && <CopyableField label="causation" value={detail.lineage.causationId} />}
        {detail.lineage.parentSpanId && <CopyableField label="parentSpan" value={detail.lineage.parentSpanId} />}
      </div>
    </div>
  );
}

function ContextTab({ detail, replayWindow }: { detail: InspectorPaneModel['detail']; replayWindow?: { start: string; end: string; windowId: string } | null }) {
  if (!detail) return null;
  const continuityColor = detail.continuityMarker ? continuityToColor(detail.continuityMarker) : null;

  // Determine replay position
  const replayPosition = useMemo(() => {
    if (!replayWindow || !replayWindow.start || !replayWindow.end) return null;
    const eventTime = new Date(detail.timestamp).getTime();
    const windowStart = new Date(replayWindow.start).getTime();
    const windowEnd = new Date(replayWindow.end).getTime();
    
    if (eventTime < windowStart) return 'before';
    if (eventTime > windowEnd) return 'after';
    return 'inside';
  }, [detail.timestamp, replayWindow]);

  return (
    <div style={{ padding: SPACING.sp3 }}>
      {/* Replay Position Context */}
      {replayWindow && (
        <div style={{ marginBottom: SPACING.sp4, padding: SPACING.sp3, backgroundColor: SEMANTIC.replayWindow.chip, border: `1px solid ${SEMANTIC.replayWindow.border}`, borderRadius: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2, marginBottom: SPACING.sp2 }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SEMANTIC.replayWindow.text,
              boxShadow: `0 0 8px ${SEMANTIC.replayWindow.text}40`,
            }} />
            <span style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: SEMANTIC.replayWindow.chipText,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}>
              Replay Position
            </span>
          </div>
          <div style={{ fontSize: TYPOGRAPHY.body.size, fontWeight: 600, color: SEMANTIC.replayWindow.chipText, marginBottom: SPACING.sp1 }}>
            {replayPosition === 'before' ? 'BEFORE WINDOW' : replayPosition === 'after' ? 'AFTER WINDOW' : 'INSIDE WINDOW'}
          </div>
          <div style={{ fontSize: TYPOGRAPHY.caption.size, color: SEMANTIC.replayWindow.chipText, opacity: 0.8 }}>
            {detail.timestamp.slice(11, 23)}
          </div>
        </div>
      )}

      {/* Continuity State - Prominent */}
      {detail.continuityMarker && continuityColor && (
        <div style={{ marginBottom: SPACING.sp4, padding: SPACING.sp3, backgroundColor: continuityColor.chip, border: `1px solid ${continuityColor.border}`, borderRadius: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sp2, marginBottom: SPACING.sp2 }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: continuityColor.text,
              boxShadow: `0 0 8px ${continuityColor.text}40`,
            }} />
            <span style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: continuityColor.chipText,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}>
              Continuity State
            </span>
          </div>
          <div style={{ fontSize: TYPOGRAPHY.heading.size, fontWeight: 600, color: continuityColor.chipText, marginBottom: SPACING.sp1 }}>
            {detail.continuityMarker}
          </div>
          {detail.refusalDetail?.rationale && (
            <div style={{ fontSize: TYPOGRAPHY.caption.size, color: continuityColor.chipText, opacity: 0.8 }}>
              {detail.refusalDetail.rationale}
            </div>
          )}
        </div>
      )}

      {/* Replay Metadata Surface */}
      {replayWindow && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <SectionHeader>Replay Metadata</SectionHeader>
          <CopyableField label="windowId" value={replayWindow.windowId} />
          <CopyableField label="windowStart" value={replayWindow.start.slice(11, 23)} />
          <CopyableField label="windowEnd" value={replayWindow.end.slice(11, 23)} />
          <div style={{ marginTop: SPACING.sp2, fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            <span style={{ color: '#5fa35f' }}>replay-safe</span> · deterministic ordering
          </div>
        </div>
      )}

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Actor / Source</SectionHeader>
        <CopyableField label="sourceModule" value={detail.sourceModule} />
        <CopyableField label="sourceTag" value={detail.sourceTag} />
        <CopyableField label="streamId" value={detail.streamId} />
      </div>

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Temporal Causality</SectionHeader>
        {detail.lineage.correlationId && <CopyableField label="correlation" value={detail.lineage.correlationId} />}
        {detail.lineage.causationId && <CopyableField label="causation" value={detail.lineage.causationId} />}
        {detail.lineage.parentSpanId && <CopyableField label="parentSpan" value={detail.lineage.parentSpanId} />}
      </div>

      {detail.safeMetadata.length > 0 && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <SectionHeader>Additional Metadata</SectionHeader>
          {detail.safeMetadata.map((meta, i) => <CopyableField key={i} label={meta.key} value={meta.value} />)}
        </div>
      )}

      {detail.proofOnly && (
        <div style={{ padding: SPACING.sp3, backgroundColor: '#1a1820', border: '1px solid #303040', borderRadius: 3, color: '#807898', fontSize: TYPOGRAPHY.caption.size }}>
          Context restricted: raw payload not available in proofOnly mode.
        </div>
      )}
    </div>
  );
}

function EvidenceTab({ detail }: { detail: InspectorPaneModel['detail'] }) {
  if (!detail) return null;
  const artifactRefs = detail.refs.filter((r) => r.refType === 'artifact');
  return (
    <div style={{ padding: SPACING.sp3 }}>
      <div style={{ marginBottom: SPACING.sp3 }}><SectionHeader>Artifact References</SectionHeader></div>
      {artifactRefs.length === 0 && <div style={{ color: NEUTRAL.text300, fontSize: TYPOGRAPHY.caption.size }}>No artifact references attached to this event.</div>}
      {artifactRefs.map((ref, i) => (
        <div key={i} style={{ marginBottom: SPACING.sp3, padding: SPACING.sp3, backgroundColor: NEUTRAL.bg600, border: `1px solid ${NEUTRAL.borderSubtle}`, borderRadius: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING.sp2 }}>
            <span style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: NEUTRAL.text400 }}>{ref.refKey}</span>
            <span style={{ fontSize: TYPOGRAPHY.micro.size, color: detail.proofOnly ? '#807898' : '#5fa35f', backgroundColor: detail.proofOnly ? '#1a1820' : '#0a2010', padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase' }}>
              {detail.proofOnly ? 'restricted' : 'available'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ref.refHash}</span>
            <CopyButton value={ref.refHash} label="artifact-hash" />
          </div>
          {ref.refPath && <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, marginTop: SPACING.sp1 }}>{ref.refPath}</div>}
        </div>
      ))}
    </div>
  );
}

function ReceiptsTab({ detail }: { detail: InspectorPaneModel['detail'] }) {
  if (!detail) return null;
  const receiptRefs = detail.refs.filter((r) => r.refType === 'receipt');
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ padding: SPACING.sp3 }}>
      <div style={{ marginBottom: SPACING.sp3 }}><SectionHeader>Receipts</SectionHeader></div>
      {receiptRefs.length === 0 && <div style={{ color: NEUTRAL.text300, fontSize: TYPOGRAPHY.caption.size }}>No receipts attached to this event.</div>}

      {receiptRefs.length > 0 && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <button
            onClick={() => setExpanded((p) => !p)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${SPACING.sp2}px 0`, backgroundColor: 'transparent', border: 'none', borderBottom: `1px solid ${NEUTRAL.borderSubtle}`, cursor: 'pointer', color: NEUTRAL.text400, fontSize: TYPOGRAPHY.labelSmall.size, fontWeight: TYPOGRAPHY.labelSmall.weight, textTransform: 'uppercase', letterSpacing: '0.05em' }}
            type="button"
            data-testid="receipt-preview-toggle"
          >
            <span>Receipts ({receiptRefs.length})</span>
            <span style={{ fontFamily: TYPOGRAPHY.mono.family }}>{expanded ? '−' : '+'}</span>
          </button>
          {expanded && (
            <div style={{ paddingTop: SPACING.sp2, transition: TRANSITION_COLLAPSE }}>
              {receiptRefs.map((ref, i) => (
                <div key={i} style={{ padding: `${SPACING.sp2}px 0`, borderBottom: `1px solid ${NEUTRAL.borderSubtle}` }}>
                  <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, marginBottom: SPACING.sp1 }}>{ref.refKey}</div>
                  <div style={{ display: 'flex', alignItems: 'center', fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ref.refHash}</span>
                    <CopyButton value={ref.refHash} label="receipt-hash" />
                  </div>
                  {ref.refPath && <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, marginTop: SPACING.sp1 }}>{ref.refPath}</div>}
                  <div style={{ marginTop: SPACING.sp1, display: 'flex', gap: SPACING.sp2 }}>
                    <span style={{ fontSize: TYPOGRAPHY.micro.size, color: '#5fa35f', backgroundColor: '#0a2010', padding: '1px 4px', borderRadius: 2 }}>signed</span>
                    <span style={{ fontSize: TYPOGRAPHY.micro.size, color: '#5fa35f', backgroundColor: '#0a2010', padding: '1px 4px', borderRadius: 2 }}>verified</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdmissibilityChainVisualization({ entries }: { entries: AdmissibilityChainEntry[] }) {
  const [hoveredEntryId, setHoveredEntryId] = useState<string | null>(null);

  // Build causation chain by ordering entries by causedByEntryId
  const buildChain = (entries: AdmissibilityChainEntry[]): AdmissibilityChainEntry[] => {
    const entryMap = new Map<string, AdmissibilityChainEntry>();
    entries.forEach(e => entryMap.set(e.entryId, e));

    // Find root entries (no causedByEntryId)
    const roots = entries.filter(e => !e.causedByEntryId);
    const ordered: AdmissibilityChainEntry[] = [];

    const traverse = (entry: AdmissibilityChainEntry) => {
      ordered.push(entry);
      const children = entries.filter(e => e.causedByEntryId === entry.entryId);
      children.forEach(traverse);
    };

    roots.forEach(traverse);
    // Add any orphaned entries at the end
    entries.forEach(e => {
      if (!ordered.includes(e)) {
        ordered.push(e);
      }
    });

    return ordered;
  };

  const chain = buildChain(entries);

  const getVerdictColor = (verdict: AdmissibilityChainEntry['verdict']) => {
    if (verdict === 'PASS') return SEMANTIC.stable;
    if (verdict === 'BLOCKED' || verdict === 'REJECTED') return SEMANTIC.refusal;
    return SEMANTIC.unresolved;
  };

  const hasReceipts = (entry: AdmissibilityChainEntry) => {
    return entry.refs.some(r => r.refType === 'receipt');
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sp2,
      padding: SPACING.sp3,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 3,
    }}>
      {chain.map((entry, index) => {
        const color = getVerdictColor(entry.verdict);
        const isHovered = hoveredEntryId === entry.entryId;
        const isRefusal = entry.isRefusal;
        const isLast = index === chain.length - 1;

        return (
          <div key={entry.entryId}>
            <div
              onMouseEnter={() => setHoveredEntryId(entry.entryId)}
              onMouseLeave={() => setHoveredEntryId(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.sp2,
                padding: SPACING.sp2,
                backgroundColor: isHovered ? NEUTRAL.bg500 : 'transparent',
                borderRadius: 2,
                cursor: 'default',
                transition: 'background-color 80ms ease-out',
              }}
            >
              {/* Node indicator */}
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: isRefusal ? SEMANTIC.refusal.text : color.text,
                border: `2px solid ${color.border}`,
                flexShrink: 0,
              }} />

              {/* Entry content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING.sp2,
                  marginBottom: SPACING.sp1,
                }}>
                  <span style={{
                    fontSize: TYPOGRAPHY.label.size,
                    fontWeight: 600,
                    color: color.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {entry.label || entry.eventType}
                  </span>
                  <span style={{
                    fontSize: TYPOGRAPHY.micro.size,
                    fontWeight: 700,
                    color: color.chipText,
                    backgroundColor: color.chip,
                    padding: '1px 4px',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}>
                    {entry.verdict}
                  </span>
                  {isRefusal && (
                    <span style={{
                      fontSize: TYPOGRAPHY.micro.size,
                      fontWeight: 700,
                      color: SEMANTIC.refusal.chipText,
                      backgroundColor: SEMANTIC.refusal.chip,
                      padding: '1px 4px',
                      borderRadius: 2,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}>
                      REFUSED
                    </span>
                  )}
                  {hasReceipts(entry) && (
                    <span style={{
                      fontSize: TYPOGRAPHY.micro.size,
                      fontWeight: 600,
                      color: SEMANTIC.stabilized.chipText,
                      backgroundColor: SEMANTIC.stabilized.chip,
                      padding: '1px 4px',
                      borderRadius: 2,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}>
                      VERIFIED
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  color: NEUTRAL.text400,
                  fontFamily: TYPOGRAPHY.mono.family,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {entry.entryId.slice(0, 16)}...
                </div>
              </div>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div style={{
                marginLeft: 3,
                height: SPACING.sp2,
                width: 2,
                backgroundColor: isRefusal ? SEMANTIC.refusal.border : color.border,
                opacity: 0.5,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function GraphTab({ detail, admissibilityChain }: { detail: InspectorPaneModel['detail']; admissibilityChain?: AdmissibilityChainEntry[] }) {
  if (!detail) return null;

  const hasAdmissibilityChain = admissibilityChain && admissibilityChain.length > 0;

  return (
    <div style={{ padding: SPACING.sp3 }}>
      {hasAdmissibilityChain && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <SectionHeader>Admissibility Chain</SectionHeader>
          <AdmissibilityChainVisualization entries={admissibilityChain} />
        </div>
      )}

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Lineage Summary</SectionHeader>
        {detail.lineage.correlationId && <CopyableField label="correlation" value={detail.lineage.correlationId} />}
        {detail.lineage.causationId && <CopyableField label="causation" value={detail.lineage.causationId} />}
        {detail.lineage.parentSpanId && <CopyableField label="parentSpan" value={detail.lineage.parentSpanId} />}
      </div>

      <div style={{ marginBottom: SPACING.sp4 }}>
        <SectionHeader>Selected Node</SectionHeader>
        <CopyableField label="eventId" value={detail.eventId} />
        <CopyableField label="eventType" value={detail.eventType} />
        <CopyableField label="severity" value={detail.severity} />
      </div>

      <div style={{ padding: SPACING.sp3, backgroundColor: NEUTRAL.bg600, border: `1px solid ${NEUTRAL.borderSubtle}`, borderRadius: 3, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.caption.size }}>
        Full lineage graph is available in the Lineage pane.
      </div>
    </div>
  );
}

export const InspectorPane: React.FC<InspectorPaneProps> = ({ model, admissibilityChain, replayWindow }) => {
  const [activeTab, setActiveTab] = useState<InspectorTab>('details');

  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Inspector error: {model.error}
      </div>
    );
  }

  if (!model.detail) {
    return (
      <div data-testid="inspector-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <div style={{
          padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
          borderBottom: `1px solid ${NEUTRAL.border}`,
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
          backgroundColor: NEUTRAL.bg600,
        }}>
          Inspector
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: SPACING.sp6, gap: SPACING.sp3 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: NEUTRAL.bg600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: SPACING.sp2,
          }}>
            <span style={{ fontSize: 24, color: NEUTRAL.text300 }}>⚡</span>
          </div>
          <div style={{ color: NEUTRAL.text400, fontSize: TYPOGRAPHY.label.size, textAlign: 'center' }}>
            Select an event to inspect
          </div>
          <div style={{ color: NEUTRAL.text300, fontSize: TYPOGRAPHY.caption.size, textAlign: 'center' }}>
            View details, context, and continuity state
          </div>
        </div>
      </div>
    );
  }

  const detail = model.detail;

  return (
    <div data-testid="inspector-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
        backgroundColor: NEUTRAL.bg600,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>Inspector</span>
        {detail.proofOnly && (
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: '#807898',
            padding: '2px 6px',
            borderRadius: 3,
            backgroundColor: '#1a1820',
            border: '1px solid #303040',
          }}>
            PROOF_ONLY
          </span>
        )}
      </div>

      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

      <div style={{ flex: 1, overflowY: 'auto', ...inspectorContentStyle(detail.eventId) }}>
        {activeTab === 'details' && <DetailsTab detail={detail} />}
        {activeTab === 'context' && <ContextTab detail={detail} replayWindow={replayWindow} />}
        {activeTab === 'evidence' && <EvidenceTab detail={detail} />}
        {activeTab === 'receipts' && <ReceiptsTab detail={detail} />}
        {activeTab === 'graph' && <GraphTab detail={detail} admissibilityChain={admissibilityChain} />}
      </div>
    </div>
  );
};
